import { create } from 'zustand';
import { coursesApi } from '../services/api';

export const useAssignmentsStore = create((set, get) => ({
  // State
  myAssignments: [],
  createdAssignments: [],
  selectedAssignment: null,
  assignmentEnvironments: {},
  assignmentSubmissions: {},
  loading: false,
  error: null,

  // Actions
  fetchMyAssignments: async (token) => {
    set({ loading: true, error: null });
    try {
      const assignments = await coursesApi.getUserAssignments(token);
      set({
        myAssignments: assignments || [],
        loading: false,
      });
      return assignments;
    } catch (error) {
      set({
        error: error.message,
        loading: false,
      });
      throw error;
    }
  },

  fetchCreatedAssignments: async (token) => {
    set({ loading: true, error: null });
    try {
      const assignments = await coursesApi.getCreatedAssignments(token);
      set({
        createdAssignments: assignments || [],
        loading: false,
      });
      return assignments;
    } catch (error) {
      set({
        error: error.message,
        loading: false,
      });
      throw error;
    }
  },

  fetchAssignmentById: async (assignmentId, token) => {
    set({ loading: true, error: null });
    try {
      const assignment = await coursesApi.getAssignment(assignmentId, token);
      set({
        selectedAssignment: assignment,
        loading: false,
      });
      return assignment;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  fetchAssignmentEnvironment: async (assignmentId, token) => {
    try {
      const status = await coursesApi.getAssignmentStatus(assignmentId, token);
      set((state) => ({
        assignmentEnvironments: {
          ...state.assignmentEnvironments,
          [assignmentId]: status.sandbox_environment || null,
        },
      }));
      return status.sandbox_environment;
    } catch (error) {
      console.error('Error fetching environment:', error);
      return null;
    }
  },

  fetchAssignmentSubmissions: async (assignmentId, token) => {
    try {
      const submissions = await coursesApi.getAssignmentSubmissions(assignmentId, token);
      set((state) => ({
        assignmentSubmissions: {
          ...state.assignmentSubmissions,
          [assignmentId]: submissions || [],
        },
      }));
      return submissions;
    } catch (error) {
      console.error('Error fetching submissions:', error);
      throw error;
    }
  },

  createAssignment: async (assignmentData, token) => {
    set({ loading: true, error: null });
    try {
      const newAssignment = await coursesApi.createAssignment(assignmentData, token);

      // Update created assignments list
      const createdAssignments = await coursesApi.getCreatedAssignments(token);
      set({
        createdAssignments: createdAssignments || [],
        loading: false,
      });

      return newAssignment;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  updateAssignment: async (assignmentId, assignmentData, token) => {
    set({ loading: true, error: null });
    try {
      const updatedAssignment = await coursesApi.updateAssignment(
        assignmentId,
        assignmentData,
        token
      );

      // Update assignment in lists
      set((state) => ({
        createdAssignments: state.createdAssignments.map((a) =>
          a.id === assignmentId ? updatedAssignment : a
        ),
        myAssignments: state.myAssignments.map((a) =>
          a.id === assignmentId ? updatedAssignment : a
        ),
        selectedAssignment:
          state.selectedAssignment?.id === assignmentId
            ? updatedAssignment
            : state.selectedAssignment,
        loading: false,
      }));

      return updatedAssignment;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  deleteAssignment: async (assignmentId, token) => {
    set({ loading: true, error: null });
    try {
      await coursesApi.deleteAssignment(assignmentId, token);

      // Remove assignment from lists
      set((state) => ({
        createdAssignments: state.createdAssignments.filter((a) => a.id !== assignmentId),
        myAssignments: state.myAssignments.filter((a) => a.id !== assignmentId),
        selectedAssignment:
          state.selectedAssignment?.id === assignmentId
            ? null
            : state.selectedAssignment,
        loading: false,
      }));

      return true;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  submitAssignment: async (assignmentId, submissionData, token) => {
    set({ loading: true, error: null });
    try {
      const submission = await coursesApi.createSubmission(
        assignmentId,
        submissionData,
        token
      );

      // Refresh submissions for this assignment
      const submissions = await coursesApi.getAssignmentSubmissions(assignmentId, token);
      set((state) => ({
        assignmentSubmissions: {
          ...state.assignmentSubmissions,
          [assignmentId]: submissions || [],
        },
        loading: false,
      }));

      return submission;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  updateSubmission: async (submissionId, submissionData, token) => {
    set({ loading: true, error: null });
    try {
      const updatedSubmission = await coursesApi.updateSubmission(
        submissionId,
        submissionData,
        token
      );

      // Update submission in cache
      set((state) => {
        const newSubmissions = { ...state.assignmentSubmissions };
        Object.keys(newSubmissions).forEach((assignmentId) => {
          newSubmissions[assignmentId] = newSubmissions[assignmentId].map((s) =>
            s.id === submissionId ? updatedSubmission : s
          );
        });

        return {
          assignmentSubmissions: newSubmissions,
          loading: false,
        };
      });

      return updatedSubmission;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  recreateEnvironment: async (environmentId, token) => {
    set({ loading: true, error: null });
    try {
      const result = await coursesApi.recreateEnvironment(environmentId, token);
      set({ loading: false });
      return result;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  destroyEnvironment: async (environmentId, token) => {
    set({ loading: true, error: null });
    try {
      await coursesApi.destroyEnvironment(environmentId, token);
      set({ loading: false });
      return true;
    } catch (error) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  selectAssignment: (assignmentId) => {
    const assignment =
      get().myAssignments.find((a) => a.id === assignmentId) ||
      get().createdAssignments.find((a) => a.id === assignmentId);
    set({ selectedAssignment: assignment });
  },

  clearSelectedAssignment: () => set({ selectedAssignment: null }),

  clearError: () => set({ error: null }),
}));
